import React from "react";
import { Box, Button, Container, Stack, Typography } from "@mui/material";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import DashboardRoundedIcon from "@mui/icons-material/DashboardRounded";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useNavigate, useSearchParams } from "react-router-dom";

import HeroSection from "../components/home/HeroSection";
import ResponsiveShowcase from "../components/home/ResponsiveShowcase";
import SellerProgramSection from "../components/home/SellerProgramSection";
import StepsSection from "../components/home/StepsSection";
import { useAuth } from "../context/AuthContext";

const WHATSAPP_URL = "https://wa.me/message/HOYXZTTFNXNZH1";

export default function HomePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading } = useAuth();
  const isAuthenticated = Boolean(user);
  const referralCode = String(searchParams.get("ref") || "").trim().toUpperCase();

  const continueAction = () => {
    if (loading) return;
    navigate(
      isAuthenticated
        ? "/dashboard"
        : referralCode
          ? `/registro?ref=${encodeURIComponent(referralCode)}`
          : "/registro",
    );
  };

  const primaryLabel = loading
    ? "Cargando..."
    : isAuthenticated
      ? "Abrir mi panel"
      : "Crear mi página gratis";

  const closingCopy = loading
    ? {
        eyebrow: "WebProfit",
        title: "Todo listo para seguir.",
        contactLabel: "Hablar con nosotros",
      }
    : isAuthenticated
      ? {
          eyebrow: "Sesión activa",
          title: "Seguimos desde tu panel.",
          contactLabel: "Necesito ayuda",
        }
      : {
          eyebrow: "Siguiente paso",
          title: "Probalo con tu negocio.",
          contactLabel: "Hablar con nosotros",
        };

  return (
    <Box component="main" sx={{ width: "100%", overflowX: "hidden", bgcolor: "#fff" }}>
      <HeroSection
        onStartClick={continueAction}
        primaryLabel={primaryLabel}
        isAuthenticated={isAuthenticated}
        userName={user?.name || ""}
        onExploreClick={() => {
          document.getElementById("como-funciona")?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }}
      />

      <StepsSection />
      <ResponsiveShowcase />

      <SellerProgramSection
        onJoinClick={continueAction}
        joinLabel="Crear mi cuenta y obtener mi enlace"
        isAuthenticated={isAuthenticated}
        authLoading={loading}
        referralCode={referralCode}
      />

      <Box
        component="section"
        aria-labelledby="home-closing-title"
        sx={{
          px: { xs: 2, md: 4 },
          pb: { xs: 5, sm: 7, md: 12 },
        }}
      >
        <Container maxWidth="lg" disableGutters>
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              borderRadius: { xs: 4, sm: 5, md: 7 },
              px: { xs: 2.2, sm: 5, md: 8 },
              py: { xs: 3.5, sm: 5, md: 7 },
              color: "#fff",
              background:
                "linear-gradient(135deg, #19242F 0%, #243746 58%, #F35A3A 160%)",
              boxShadow: "0 28px 70px rgba(25,36,47,.22)",
              "&::before": {
                content: '\"\"',
                position: "absolute",
                width: 320,
                height: 320,
                borderRadius: "50%",
                right: -100,
                top: -150,
                background: "rgba(243,90,58,.28)",
                filter: "blur(2px)",
              },
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              alignItems={{ xs: "flex-start", md: "center" }}
              justifyContent="space-between"
              spacing={{ xs: 2.5, md: 4 }}
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Box sx={{ maxWidth: 680 }}>
                <Typography
                  sx={{
                    color: "#FFB49F",
                    fontWeight: 900,
                    letterSpacing: ".12em",
                    textTransform: "uppercase",
                    fontSize: ".78rem",
                    mb: 1.25,
                  }}
                >
                  {closingCopy.eyebrow}
                </Typography>
                <Typography
                  id="home-closing-title"
                  component="h2"
                  variant="h3"
                  sx={{
                    fontWeight: 950,
                    letterSpacing: "-.045em",
                    lineHeight: 1.05,
                    fontSize: { xs: "1.72rem", sm: "2.5rem", md: "3.15rem" },
                  }}
                >
                  {closingCopy.title}
                </Typography>
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                sx={{ width: { xs: "100%", md: "auto" } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={continueAction}
                  disabled={loading}
                  startIcon={isAuthenticated ? <DashboardRoundedIcon /> : undefined}
                  endIcon={!isAuthenticated ? <ArrowForwardRoundedIcon /> : undefined}
                  sx={{
                    minHeight: { xs: 49, sm: 54 },
                    px: { xs: 2.4, sm: 3 },
                    borderRadius: 999,
                    bgcolor: "#F35A3A",
                    fontWeight: 900,
                    textTransform: "none",
                    boxShadow: "0 14px 30px rgba(243,90,58,.3)",
                    "&:hover": { bgcolor: "#DF4728" },
                  }}
                >
                  {primaryLabel}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<WhatsAppIcon />}
                  onClick={() => (window.location.href = WHATSAPP_URL)}
                  sx={{
                    minHeight: { xs: 47, sm: 54 },
                    px: { xs: 2.2, sm: 2.5 },
                    borderRadius: 999,
                    color: "#fff",
                    borderColor: "rgba(255,255,255,.36)",
                    fontWeight: 850,
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "#fff",
                      bgcolor: "rgba(255,255,255,.08)",
                    },
                  }}
                >
                  {closingCopy.contactLabel}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
